/**
 * Format AI-generated reflection text into structured, elegant display
 * Converts markdown-style text into React-friendly formatted content
 */

/**
 * Parse reflection text and convert to formatted structure
 * @param {string} text - Raw reflection text
 * @returns {Array} - Array of formatted sections
 */
export const parseReflectionText = (text) => {
  if (!text) return [];

  const sections = [];
  const lines = text.split('\n');
  let currentSection = null;
  let currentList = null;

  lines.forEach((line) => {
    const trimmed = line.trim();

    // Skip empty lines between sections
    if (!trimmed && !currentList) {
      return;
    }

    // Detect section headers (bold text with ** or numbered sections)
    const headerMatch = trimmed.match(/^\*\*(\d+\.\s*)?(.+?):\*\*$/);
    if (headerMatch) {
      // Save previous section if exists
      if (currentSection) {
        sections.push(currentSection);
      }
      if (currentList) {
        currentList = null;
      }

      currentSection = {
        type: 'section',
        title: headerMatch[2].trim(),
        content: [],
      };
      return;
    }

    // Detect list items (starts with - or •)
    const listMatch = trimmed.match(/^[-•]\s*\*\*(.+?):\*\*\s*(.+)$/);
    if (listMatch) {
      if (!currentList) {
        currentList = {
          type: 'list',
          items: [],
        };
        if (currentSection) {
          currentSection.content.push(currentList);
        }
      }
      currentList.items.push({
        label: listMatch[1].trim(),
        text: listMatch[2].trim(),
      });
      return;
    }

    // Regular list item without bold label
    const simpleListMatch = trimmed.match(/^[-•]\s*(.+)$/);
    if (simpleListMatch) {
      if (!currentList) {
        currentList = {
          type: 'list',
          items: [],
        };
        if (currentSection) {
          currentSection.content.push(currentList);
        }
      }
      currentList.items.push({
        text: simpleListMatch[1].trim(),
      });
      return;
    }

    // Regular paragraph text
    if (trimmed && currentSection) {
      // End current list if we're starting a new paragraph
      if (currentList) {
        currentList = null;
      }

      currentSection.content.push({
        type: 'paragraph',
        text: trimmed,
      });
    }
  });

  // Add last section
  if (currentSection) {
    sections.push(currentSection);
  }

  return sections;
};

/**
 * Format reflection text for display (fallback for simple formatting)
 * @param {string} text - Raw reflection text
 * @returns {string} - Formatted text with proper line breaks
 */
export const formatReflectionText = (text) => {
  if (!text) return '';

  return text
    .replace(/\*\*(\d+\.\s*)?(.+?):\*\*/g, '\n\n$2:\n') // Section headers
    .replace(/^[-•]\s*/gm, '  • ') // List items
    .replace(/\n{3,}/g, '\n\n') // Remove excessive line breaks
    .trim();
};
